import { isDefined } from "@clipboard-health/util-ts";
import { Box, ButtonBase, Stack } from "@mui/material";
import { useWorkerProfilePicture } from "@src/appV2/Agents/api/useWorkerProfilePicture";
import { useIsWorkplaceReviewUXUpdateEnabled } from "@src/appV2/PostShiftReview/hooks/useIsWorkplaceReviewUXUpdateEnabled";
import { WorkerAvatar } from "@src/appV2/redesign/components/WorkerAvatar";
import { useWorkplaceModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useWorkplaceModalsDataContext";
import { CbhIcon, Text, useModalState } from "@src/appV2/redesign/system/ui-components";
import { useDeleteCommentReaction } from "@src/appV2/redesign/Workplace/Review/api/useDeleteCommentReaction";
import { usePostCommentReaction } from "@src/appV2/redesign/Workplace/Review/api/usePostCommentReaction";
import { type WorkplaceCommentType } from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { formatDistanceToNow, parseISO } from "date-fns";

import { CommentReactionSource, Reaction } from "../types";
import { ConfirmRemoveCommentBottomSheet } from "./ConfirmRemoveCommentBottomSheet";

function getLikeIconType(params: {
  isReviewUXUpdateEnabled: boolean;
  isLiked: boolean;
}): "thumb-up-filled" | "thumb-up-outlined" | "like-filled" | "like-outlined" {
  const { isReviewUXUpdateEnabled, isLiked } = params;
  if (isReviewUXUpdateEnabled) {
    return isLiked ? "thumb-up-filled" : "thumb-up-outlined";
  }

  return isLiked ? "like-filled" : "like-outlined";
}

interface InlineReplyCardProps {
  reply: WorkplaceCommentType;
  workplaceId: string;
  viewerIsAuthor: boolean;
  onLikeChanged: (comment: WorkplaceCommentType) => void;
  onRemoved: (commentId: string) => void;
}

export function InlineReplyCard(props: InlineReplyCardProps) {
  const { reply, workplaceId, viewerIsAuthor, onLikeChanged, onRemoved } = props;
  const { workerInfo, text, createdAt, workerReaction, aggregates } = reply.attributes;

  const isLiked = workerReaction === Reaction.LIKE;
  const isDisliked = workerReaction === Reaction.DISLIKE;
  const { isEnabled: isReviewUXUpdateEnabled } = useIsWorkplaceReviewUXUpdateEnabled();
  const { reviewsViewContext } = useWorkplaceModalsDataContext();
  const removeModalState = useModalState();

  const { data: profilePicture, isInitialLoading: isProfilePictureLoading } =
    useWorkerProfilePicture(String(workerInfo.id), {
      enabled: isDefined(workerInfo.id),
    });

  const { mutate: postCommentReaction } = usePostCommentReaction(
    {
      workplaceId,
      commentId: reply.id,
      source: CommentReactionSource.INLINE_REPLY,
      viewContext: reviewsViewContext,
    },
    {
      onSuccess: (data) => {
        onLikeChanged(data.data);
      },
    }
  );

  const { mutate: deleteCommentReaction } = useDeleteCommentReaction(
    {
      workplaceId,
      commentId: reply.id,
      source: CommentReactionSource.INLINE_REPLY,
      viewContext: reviewsViewContext,
    },
    {
      onSuccess: (data) => {
        onLikeChanged(data.data);
      },
    }
  );

  function handleLikeToggle() {
    if (isLiked) {
      deleteCommentReaction({ reactionType: Reaction.LIKE });
    } else {
      postCommentReaction({ reaction: Reaction.LIKE });
    }
  }

  function handleDislikeToggle() {
    if (isDisliked) {
      deleteCommentReaction({ reactionType: Reaction.DISLIKE });
    } else {
      postCommentReaction({ reaction: Reaction.DISLIKE });
    }
  }

  return (
    <>
      <Stack spacing={2}>
        <Stack direction="row" spacing={3} alignItems="flex-start">
          <WorkerAvatar
            profilePictureUrl={profilePicture?.distribution_url}
            isLoading={isProfilePictureLoading}
            size="small"
          />

          <Box
            sx={(theme) => ({
              backgroundColor: theme.palette.background.tertiary,
              borderRadius: 3,
              flex: 1,
              px: 5,
            })}
          >
            <Stack direction="row" justifyContent="space-between" sx={{ marginBottom: 1 }}>
              <Text semibold variant="body2">
                {workerInfo.name}
              </Text>
              <Text variant="caption" color={(theme) => theme.palette.text.secondary}>
                {formatDistanceToNow(parseISO(createdAt))} ago
              </Text>
            </Stack>
            <Text variant="body2">{text}</Text>
          </Box>
        </Stack>

        <Stack direction="row" spacing={3} alignItems="center" sx={{ paddingLeft: 11 }}>
          <ButtonBase
            aria-label="Like reaction"
            aria-pressed={isLiked}
            sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center" }}
            onClick={(event) => {
              event.stopPropagation();
              handleLikeToggle();
            }}
          >
            <CbhIcon
              type={getLikeIconType({ isReviewUXUpdateEnabled, isLiked })}
              size="small"
              color={(theme) => theme.palette.text.secondary}
            />
            <Text variant="caption" color={(theme) => theme.palette.text.secondary}>
              {aggregates.totalLikes}
            </Text>
          </ButtonBase>

          {isReviewUXUpdateEnabled && (
            <ButtonBase
              aria-label="Dislike reaction"
              aria-pressed={isDisliked}
              sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center" }}
              onClick={(event) => {
                event.stopPropagation();
                handleDislikeToggle();
              }}
            >
              <CbhIcon
                type={isDisliked ? "thumb-down-filled" : "thumb-down-outlined"}
                size="small"
                color={(theme) => theme.palette.text.secondary}
              />
              <Text variant="caption" color={(theme) => theme.palette.text.secondary}>
                {aggregates.totalDislikes}
              </Text>
            </ButtonBase>
          )}

          {viewerIsAuthor && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ cursor: "pointer" }}
              onClick={(event) => {
                event.stopPropagation();
                removeModalState.openModal();
              }}
            >
              <CbhIcon type="trash" size="small" color={(theme) => theme.palette.error.main} />
            </Stack>
          )}
        </Stack>
      </Stack>

      {viewerIsAuthor && (
        <ConfirmRemoveCommentBottomSheet
          modalState={removeModalState}
          commentId={reply.id}
          workplaceId={workplaceId}
          onRemoved={onRemoved}
        />
      )}
    </>
  );
}
